import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { ReplaySubject, Observable, from, BehaviorSubject } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { Query } from '@angular/fire/firestore';
import firebase from 'firebase/app';
import { AngularFireAuth } from '@angular/fire/auth';

import { AuthService, AuthUser } from '@monorepo/ui/auth';
import { FuiModalService } from 'ngx-fomantic-ui';

import { Booking, UserDetail, BookingBundle } from './../../../models';
import {
  FavouriteScreenItem,
  FavouriteScreens,
  ScreenListing,
} from '../models';
import { ListingItemModal } from '../../screen-listing-item/components/listing-item-model';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  authUserId: string;
  isUserAdmin = false;
  private currentBookedScreens: ScreenListing[] = [];
  currentBookedScreens$: BehaviorSubject<ScreenListing[]> = new BehaviorSubject(
    []
  );
  private currentBooking: BookingBundle = new BookingBundle();
  currentBooking$: ReplaySubject<BookingBundle> = new ReplaySubject();
  userDetail: UserDetail;

  constructor(
    private authService: AuthService,
    private afs: AngularFirestore,
    private modalService: FuiModalService,
    private afAuth: AngularFireAuth
  ) {
    this.authService.user.subscribe({
      next: (user: AuthUser): void => {
        if (user) {
          this.authUserId = user.uid;
          this.isUserAdmin = user.isAdmin;
          this.getUserInfo().subscribe((userObject: any) => {
            this.userDetail = userObject?.userDetail;
          });
        }
      },
    });
    this.currentBookedScreens$.next([]);
  }

  public addScreenToBooking(
    screenListing: ScreenListing,
    ownerId: string
  ): void {
    if (!this.currentBookedScreens.includes(screenListing)) {
      this.currentBookedScreens.push(screenListing);
      this.currentBooking.bookings.push(
        new Booking(
          this.afs.createId(),
          this.authUserId,
          ownerId,
          screenListing.firestickId,
          0,
          {
            listingId: screenListing.id,
            screenName: screenListing.screenName,
            screenLocation: screenListing.address,
            city: screenListing.city,
            screenSize: screenListing.screenHeight,
            screenRate: screenListing.screenPrice,
            screenImgUrl: screenListing.screenImages[0].url,
          }
        )
      );
    }
    this.currentBookedScreens$.next(this.currentBookedScreens);
    this.currentBooking$.next(this.currentBooking);
  }

  public removeScreenFromBooking(screenListing: ScreenListing): void {
    const cbsIndex = this.currentBookedScreens.findIndex(
      (cbs) => cbs.firestickId === screenListing.firestickId
    );
    if (cbsIndex !== -1) {
      this.currentBookedScreens.splice(cbsIndex, 1);
    }
    const bookingIndex = this.currentBooking.bookings.findIndex(
      (spd) => spd.fireStickId === screenListing.firestickId
    );
    if (bookingIndex !== -1) {
      this.currentBooking.cost -=
        this.currentBooking.bookings[bookingIndex].cost;
      this.currentBooking.bookings.splice(bookingIndex, 1);
    }
    this.currentBookedScreens$.next(this.currentBookedScreens);
    this.currentBooking$.next(this.currentBooking);
  }

  public clearCurrentBooking(): void {
    this.currentBooking = new BookingBundle();
    this.currentBooking$.next(this.currentBooking);
    this.currentBookedScreens = [];
    this.currentBookedScreens$.next(this.currentBookedScreens);
  }

  public getCurrentBookedScreens(): any {
    return this.currentBookedScreens$.asObservable();
  }

  public updateImpressionCount(
    firestickId: string,
    impressionCount: number,
    rate: number
  ): void {
    let newScreenCost = 0;
    let oldCost = 0;
    this.currentBooking.bookings.forEach((booking: Booking, idx: number) => {
      if (booking.fireStickId === firestickId) {
        const screenPlayDetailToUpdate = this.currentBooking.bookings[idx];
        screenPlayDetailToUpdate.impressions = impressionCount;
        oldCost = screenPlayDetailToUpdate.cost;
        const adPerMin = screenPlayDetailToUpdate.length;
        newScreenCost = screenPlayDetailToUpdate.cost =
          adPerMin * rate * impressionCount;
      }
    });
    this.currentBooking.cost -= oldCost;
    this.currentBooking.cost += newScreenCost;
    this.currentBooking$.next(this.currentBooking);
  }

  public updateIntervalCount(firestickId: string, intervalCount: number): void {
    const bookingIndex = this.currentBooking.bookings.findIndex(
      (booking) => booking.fireStickId === firestickId
    );
    if (bookingIndex !== -1) {
      this.currentBooking.bookings[bookingIndex].interval = intervalCount;
    }
  }

  public updateStartDate(
    firestickId: string,
    startDate: firebase.firestore.Timestamp
  ): void {
    const bookingIndex = this.currentBooking.bookings.findIndex(
      (booking) => booking.fireStickId === firestickId
    );
    if (bookingIndex !== -1) {
      this.currentBooking.bookings[bookingIndex].startDate = startDate;
    }
  }

  public updateAdUrl(
    firestickId: string,
    adURL: string,
    adDuration: number,
    advertisementId: string,
    fileType: string
  ): void {
    this.clearCost(firestickId);
    const bookingIndex = this.currentBooking.bookings.findIndex(
      (spd) => spd.fireStickId === firestickId
    );

    if (bookingIndex !== -1) {
      this.currentBooking.bookings[bookingIndex].adUrl = adURL;
      this.currentBooking.bookings[bookingIndex].length = adDuration;
      this.currentBooking.bookings[bookingIndex].fileType = fileType;
      this.currentBooking.bookings[bookingIndex].advertisementId =
        advertisementId;
    }
  }

  public getCurrenBooking(): Observable<BookingBundle> {
    return this.currentBooking$.asObservable();
  }

  public getUserInfo(): Observable<any> {
    return this.afs.doc('sassUser/' + this.authUserId).valueChanges();
  }

  public checkoutBooking(): void {
    const jsonBookings = this.currentBooking.bookings.map((obj) => {
      return Object.assign({}, obj);
    });
    this.currentBooking.bookings = jsonBookings;
    this.currentBooking.bookings.forEach((booking: Booking) => {
      this.afs.doc(`/bookings/${booking.id}`).set({ ...booking });
    });
    this.clearCurrentBooking();
  }

  public getAllAds(): Observable<any[]> {
    return this.afs
      .collection(`advertisements`, (ref) => {
        let query: Query = ref;
        query = query
          .where('uid', '==', this.authUserId)
          .where('status', '==', 'Approved');
        return query;
      })
      .valueChanges();
  }

  public addOrder(order: any): Observable<any> {
    return from(
      this.afs.collection(`users/${this.authUserId}/orders`).add({
        ...order,
      })
    ).pipe(
      map((action) => {
        action.update({
          id: action.id,
        });
        return action.id;
      })
    );
  }

  public clearCost(firestickId: string): void {
    const bookingIndex = this.currentBooking.bookings.findIndex(
      (spd) => spd.fireStickId === firestickId
    );
    this.currentBooking.bookings[bookingIndex].adUrl = null;
    this.currentBooking.bookings[bookingIndex].length = 0;
    this.currentBooking.bookings[bookingIndex].advertisementId = null;
    this.currentBooking.bookings[bookingIndex].impressions = 0;
    this.currentBooking.bookings[bookingIndex].interval = 0;
    this.currentBooking.cost -= this.currentBooking.bookings[bookingIndex].cost;
    this.currentBooking.bookings[bookingIndex].cost = 0;
  }

  public updateisFlexibleDate(isFlexible: boolean, firestickId: string): void {
    const spdIndex = this.currentBooking.bookings.findIndex(
      (booking) => booking.fireStickId === firestickId
    );
    if (spdIndex !== -1) {
      this.currentBooking.bookings[spdIndex].isFlexibleDate = isFlexible;
    }
  }

  public updateEndDate(
    endDate: firebase.firestore.Timestamp,
    firestickId: string
  ): void {
    const spdIndex = this.currentBooking.bookings.findIndex(
      (booking) => booking.fireStickId === firestickId
    );
    if (spdIndex !== -1) {
      this.currentBooking.bookings[spdIndex].endDate = endDate;
    }
  }

  public openFavouritesModal(
    title: string,
    screen: ScreenListing,
    modalSize?: any
  ): any {
    return this.modalService.open(
      new ListingItemModal(title, screen, modalSize)
    );
  }

  public createFavouriteList(
    favourite: FavouriteScreens,
    favouriteItem: FavouriteScreenItem
  ): Observable<any> {
    return this.afAuth.authState.pipe(
      mergeMap((): any => {
        favourite.id = this.afs.createId();
        favouriteItem.favouriteListId = favourite.id;
        favouriteItem.screen = this.afs
          .collection('screens')
          .doc(favouriteItem.screenId).ref;
        favourite.screens.push(favouriteItem);
        return from(
          this.afs
            .collection(`users/${this.authUserId}/favourites`)
            .doc(favourite.id)
            .set({ ...favourite })
        );
      })
    );
  }

  public updateFavouriteList(
    id: string,
    favouriteItem: FavouriteScreenItem
  ): Observable<any> {
    return this.afAuth.authState.pipe(
      mergeMap((): any => {
        favouriteItem.favouriteListId = id;
        favouriteItem.screen = this.afs
          .collection('screens')
          .doc(favouriteItem.screenId).ref;
        return from(
          this.afs
            .collection(`users/${this.authUserId}/favourites`)
            .doc(id)
            .update({
              screens: firebase.firestore.FieldValue.arrayUnion(favouriteItem),
            })
        );
      })
    );
  }

  public getFavouriteList(): Observable<any> {
    return this.afAuth.authState.pipe(
      mergeMap((authUser: firebase.User): any => {
        if (authUser !== undefined) {
          const authUid = authUser?.uid;
          return this.afs
            .collection(`users/${authUid}/favourites`)
            .valueChanges();
        }
      })
    );
  }

  public removeItemFromFavouriteList(
    favId: string,
    favouriteItem: FavouriteScreenItem
  ): Observable<any> {
    return this.afAuth.authState.pipe(
      mergeMap((authUser: firebase.User): any => {
        const authUid = authUser.uid;
        return from(
          this.afs
            .collection(`users/${authUid}/favourites`)
            .doc(favId)
            .update({
              screens: firebase.firestore.FieldValue.arrayRemove(favouriteItem),
            })
        );
      })
    );
  }
}
